import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Merge Tailwind class names while resolving conflicts.
 *
 * Used by every component as the standard `className` builder.
 *
 *   <div className={cn("px-2 py-1", isActive && "bg-primary-500", className)} />
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Convert a Date or Unix-second timestamp into a localized Arabic-Iraq string.
 */
export function formatDateAR(value: number | Date | null | undefined): string {
  if (value == null) return "";
  const date = typeof value === "number" ? new Date(value * 1000) : value;
  return new Intl.DateTimeFormat("ar-IQ", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

/**
 * Format a number using Arabic-Indic numerals (٠-٩).
 */
export function formatNumberAR(n: number): string {
  return new Intl.NumberFormat("ar-IQ").format(n);
}

/**
 * Format a number using Latin digits (0-9) but with Arabic separators.
 * Use for technical fields like weapon serial numbers.
 */
export function formatNumberLatin(n: number): string {
  return new Intl.NumberFormat("en-US").format(n);
}

export function uniq<T>(arr: T[]): T[] {
  return Array.from(new Set(arr));
}

export function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}
